<template>
    <div testid="ls-datepicker__container">
        <!-- tabindex required for tooltip to function on click on iOS -->
        <label class="ls-label" testid="ls-datepicker__label">
            {{ settings.label }}
            <span
                tabindex="0"
                :aria-label="tooltip.text"
                :data-balloon-pos="tooltip.pos"
                data-balloon-length="large"
                ><!--
            |--><i class="fa fa-question-circle ls-color--blue"></i
                ><!--
        |--></span
            >
        </label>
        <div class="ls-input__wrapper">
            <span class="ls-input__icon" aria-hidden="true">
                <i class="fa fa-calendar fa-fw"></i>
            </span>
            <datepicker
                :value="datepickerSelectedDate"
                :highlighted="settings.highlighted"
                :disabled-dates="settings.disabled"
                :format="settings.format"
                :wrapper-class="settings.wrapperClass"
                :input-class="settings.class"
                @selected="dateSelected"
            ></datepicker>
        </div>
    </div>
</template>

<script>
/** Vue2js datepicker component.
 * Example usage:
 * <ls-datepicker
 *    :datepickerDate="'2017-04-20" = Selected date in YYYY-MM-DD format
 *    :options="datepickerOptions"  * Datepicker options
 *    @dateSelected="setDate($event)">    * Method to call when date is selected. will receive date selected.
 * </ls-datepicker>
 *
 * datepickerDate: Required, YYYY-MM-DD string
 * datepickerOptions:
 *    disabled = {...} // Optional, disable specific dates. use carefully, minDate and maxDates will be overwritten
 *       // if you send this var. check datepicker documentation
 *       // default: minDate, maxDate and weekends are disabled
 *    minDate = '2017-04-19' // Optional, all dates before this one will be disabled, default: 2 business day from today
 *    maxDate = '2017-04-19' // Optional, all dates starting from this one will be disabled, default: 1 years from today
 *    format = 'YYYY-MM-DD' // Optional, display date format, default: 'D, MMM dd, yyyy'
 *    highlightDate = '2017-04-22' // Optional, hightlight a specific date, usefull for edit, default: empty
 *     wrapperClass = 'someClassWrapper' // Optional, wrapper class for full datepicker for targetted styling
 *    class = 'someClass'  // Optional, classes for input form displaying result date, default: 'ls-input'
 *    label = 'Select date' // Optional, label for input. Default: 'Preferred start date'
 *
 */
import _ from 'lodash';
import moment from 'moment';
import business from 'moment-business';
import { mapState } from 'vuex';
import datepicker from 'vuejs-datepicker';
import lsTrackService from '@/services/ls-track/ls-track.service';

export default {
    components: {
        datepicker,
    },

    props: {
        options: {
            type: Object,
            required: false,
        },
        datepickerDate: {
            type: String,
            required: true,
        },
        disabledDays: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            settings: {
                highlighted: {
                    dates: [],
                },
                disabled: {
                    to: this.disabledToDate(),
                    from: this.disabledFromDate(),
                    days: this.disabledDays,
                },
                wrapperClass: 'ls-datepicker',
                class: 'ls-input has-icon',
                label: 'Preferred start date',
                format: 'D, MMM dd, yyyy',
            },
        };
    },

    computed: {
        ...mapState(['screenSize']),
        datepickerSelectedDate() {
            return this.options.minDate
                ? this.getDate(this.options.minDate)
                : this.getDate(this.datepickerDate);
        },

        minDateDaysFromNow() {
            return moment(this.options.minDate).diff(moment(), 'days');
        },

        tooltip() {
            const pos = this.screenSize.xs ? 'up' : 'right';
            return {
                text: `A minimum of ${
                    this.options.minDate ? this.minDateDaysFromNow : '2'
                } business days is required to schedule your first service. Not all days of the week are available in all locations.`,
                pos,
            };
        },
    },

    created() {
        // setting default values:
        this.settings = _.defaults({}, this.options, this.settings);
    },
    methods: {
        dateSelected(date) {
            lsTrackService.track({
                type: 'change',
                target: 'order-start-date-datepicker',
                data: {
                    value: moment(date).format('YYYY-MM-DD'),
                },
            });

            this.$emit('dateSelected', moment(date).format('YYYY-MM-DD'));
        },
        getDate(dateString) {
            return moment(dateString).startOf('day').toDate();
        },
        disabledToDate() {
            return _.get(this, 'options.minDate')
                ? this.getDate(this.options.minDate)
                : moment(business.addWeekDays(moment().startOf('day'), 2)).toDate(); // 2 business days from now
        },
        disabledFromDate() {
            return _.get(this, 'options.maxDate')
                ? this.getDate(this.options.maxDate)
                : moment().add(1, 'year').toDate(); // 1 years from now
        },
    },
};
</script>

<style lang="scss">
.vdp-datepicker.ls-datepicker {
    .vdp-datepicker__calendar {
        transform: translateY(-1px); // force top-edge to align with bottom edge of ls-input
        border-color: palette(gray);
        font-size: rem(-1);
        width: 250px;

        header {
            .prev {
                &:after {
                    border-right-color: palette(black);
                }
                &.disabled:after {
                    border-right-color: palette(gray);
                }
            }

            .next {
                &:after {
                    border-left-color: palette(black);
                }
                &.disabled:after {
                    border-left-color: palette(gray);
                }
            }

            .up {
                font-weight: 700;
            }

            .prev:not(.disabled),
            .next:not(.disabled),
            .up:not(.disabled) {
                &:hover {
                    background: palette(green, x-light); // same as `.ls-button--inverse:hover`
                }
            }
        }

        .disabled {
            color: palette(gray, light);
            background-color: palette(gray, xx-light);
        }

        .cell {
            &:not(.blank):not(.disabled).day,
            &:not(.blank):not(.disabled).month,
            &:not(.blank):not(.disabled).year {
                &:hover {
                    background-color: palette(green, xx-light);
                    border-color: palette(green, xx-light);
                    border-radius: 3px;
                }
            }

            &.day,
            &:not(.selected):not(.disabled).day {
                color: palette(blue, dark);
            }

            &.disabled {
                color: palette(gray, light);
            }

            &.selected {
                font-weight: 800;
                color: palette(white);
                background: palette(green);

                &:hover,
                &.highlighted {
                    background-color: palette(green) !important;
                }
            }

            &.today {
                position: relative; // for positioning before element
                &.disabled {
                    color: palette(gray);
                    background-color: palette(gray, x-light);
                }
            }

            &.highlighted {
                background: palette(green);
            }

            &.gray {
                color: palette(gray);
            }

            &.day-header {
                font-weight: 700;
                font-size: rem(-2);
            }

            &.cell {
                line-height: 35px;
                height: 35px;
            }
        }
    }
}

[data-balloon]:after {
    text-align: center;
    font-weight: 600;
}
</style>
